import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { ApiError, apiFetch } from '../../api/client.js';
import { useChangeRole, useRevokeMember, useUpsertMember } from '../../api/admin-members.js';
import type { Membership, Tenant } from '../../api/types.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ChangeRoleModal } from './ChangeRoleModal.js';
import { InviteMemberModal } from './InviteMemberModal.js';
import { MemberRow, type AggregatedMember } from './MemberRow.js';
import { PageWrapper } from '../../shell/PageWrapper.js';

interface Props {
  tenants: Tenant[];
  memberships: Membership[];
}

type TabKey = 'all' | 'active' | 'invited' | 'disabled';

interface MemberRowEntry {
  member: Membership;
  tenant: Tenant;
}

function aggregateByUser(entries: MemberRowEntry[]): AggregatedMember[] {
  const acc = new Map<string, AggregatedMember>();
  for (const e of entries) {
    const existing = acc.get(e.member.uid);
    if (existing) {
      existing.memberships.push({ membership: e.member, tenant: e.tenant });
    } else {
      acc.set(e.member.uid, {
        uid: e.member.uid,
        memberships: [{ membership: e.member, tenant: e.tenant }],
      });
    }
  }
  return Array.from(acc.values()).sort((a, b) => a.uid.localeCompare(b.uid));
}

export function AdminMembersScreen({ tenants, memberships }: Props) {
  const [sp, setSp] = useSearchParams();
  const initialTenantId = sp.get('tenantId') ?? 'all';

  // Restrict scope to tenants the caller has any membership on. The
  // BE further enforces `group_admin` at request time; this just keeps
  // the table tidy and avoids fanning 404s.
  const callerTenantIds = new Set(memberships.map((m) => m.tenantId));
  const tenantsInScope = useMemo(
    () => tenants.filter((t) => callerTenantIds.has(t.tenantId)),
    [tenants, memberships],
  );

  const [tenantFilter, setTenantFilter] = useState<string>(
    tenantsInScope.some((t) => t.tenantId === initialTenantId) ? initialTenantId : 'all',
  );
  const [tab, setTab] = useState<TabKey>('all');
  const [toast, setToast] = useState<string | null>(null);
  const [editing, setEditing] = useState<MemberRowEntry | null>(null);
  const [revoking, setRevoking] = useState<MemberRowEntry | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const queriedIds =
    tenantFilter === 'all' ? tenantsInScope.map((t) => t.tenantId) : [tenantFilter];

  const memberQueries = useQueries({
    queries: queriedIds.map((tid) => ({
      queryKey: ['tenant-members', tid] as const,
      queryFn: () => apiFetch<Membership[]>(`/tenants/${encodeURIComponent(tid)}/members`),
      staleTime: 30_000,
    })),
  });

  const rows: MemberRowEntry[] = useMemo(() => {
    const acc: MemberRowEntry[] = [];
    queriedIds.forEach((tid, i) => {
      const tenant = tenants.find((t) => t.tenantId === tid);
      const data = memberQueries[i]?.data;
      if (!tenant || !data) return;
      for (const m of data) acc.push({ member: m, tenant });
    });
    acc.sort((a, b) => b.member.grantedAt.localeCompare(a.member.grantedAt));
    return acc;
  }, [memberQueries.map((q) => q.dataUpdatedAt).join('|'), tenants, queriedIds.join('|')]);

  // Aggregate the per-membership rows into one entry per user (REQ-FE-029h).
  // "Active" = at least one membership without expiresAt; "Invited" = all
  // memberships have a future expiresAt (treat as pending acceptance);
  // "Disabled" = all memberships have a past expiresAt. The Membership
  // shape today lacks an explicit `status`/`enabled` boolean so these
  // buckets are computed from `expiresAt` until BE adds one.
  const aggregated = useMemo(() => aggregateByUser(rows), [rows]);
  const now = Date.now();
  function classify(m: AggregatedMember): TabKey {
    const exps = m.memberships.map((mm) => mm.membership.expiresAt);
    if (exps.every((e) => e !== null && new Date(e).getTime() < now)) return 'disabled';
    if (exps.every((e) => e !== null && new Date(e).getTime() > now)) return 'invited';
    return 'active';
  }
  const tabCounts: Record<TabKey, number> = {
    all: aggregated.length,
    active: 0,
    invited: 0,
    disabled: 0,
  };
  for (const m of aggregated) {
    const c = classify(m);
    tabCounts[c]++;
  }
  const visible = aggregated.filter((m) => tab === 'all' || classify(m) === tab);

  const isLoading = memberQueries.some((q) => q.isLoading);
  const isError = memberQueries.length > 0 && memberQueries.every((q) => q.isError);

  const upsertMut = useUpsertMember();
  const changeRoleMut = useChangeRole();
  const revokeMut = useRevokeMember();

  function applyTenantFilter(next: string) {
    setTenantFilter(next);
    const params = new URLSearchParams(sp);
    if (next === 'all') params.delete('tenantId');
    else params.set('tenantId', next);
    setSp(params, { replace: true });
  }

  async function handleChangeRole(role: Membership['role']) {
    if (!editing) return;
    setEditError(null);
    try {
      await changeRoleMut.mutateAsync({
        uid: editing.member.uid,
        tenantId: editing.member.tenantId,
        role,
      });
      setToast(`Rôle mis à jour pour ${editing.member.uid}.`);
      setEditing(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setEditError('Vous n’avez pas les droits pour modifier ce rôle.');
      } else {
        setEditError('Échec de la mise à jour.');
      }
    }
  }

  async function handleRevoke() {
    if (!revoking) return;
    setRevokeError(null);
    try {
      await revokeMut.mutateAsync({
        uid: revoking.member.uid,
        tenantId: revoking.member.tenantId,
      });
      setToast(`Membre révoqué.`);
      setRevoking(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setRevokeError('Impossible : dernier administrateur global.');
      } else if (e instanceof ApiError && e.status === 403) {
        setRevokeError('Vous n’avez pas les droits pour révoquer ce membre.');
      } else {
        setRevokeError('Échec de la révocation.');
      }
    }
  }

  async function handleInvite(payload: {
    tenantId: string;
    email: string;
    role: Membership['role'];
    expiresAt: string | null;
  }) {
    setInviteError(null);
    try {
      const result = await upsertMut.mutateAsync(payload);
      setToast(
        result.status === 201
          ? `Invitation envoyée à ${payload.email}.`
          : `Membre mis à jour : ${payload.email}.`,
      );
      setInviting(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setInviteError(
          'Cet utilisateur doit d’abord se connecter via SSO Altyor avant d’être invité.',
        );
      } else if (e instanceof ApiError && e.status === 403) {
        setInviteError('Vous n’avez pas les droits pour inviter ce membre.');
      } else if (e instanceof ApiError && e.status === 400) {
        setInviteError('Requête invalide. Vérifiez les champs.');
      } else {
        setInviteError('Échec de l’invitation.');
      }
    }
  }

  return (
    <PageWrapper>
      <section className="annuaire" aria-label="Administration des membres">
        <header className="invoices__head">
          <div>
            <div className="eyebrow">Administration</div>
            <h1 className="display invoices__title">Membres</h1>
          </div>
          <div className="invoices__head-actions">
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                setInviteError(null);
                setInviting(true);
              }}
              data-testid="admin-invite-open"
            >
              Inviter un membre
            </button>
          </div>
        </header>

        <div className="invoices__advanced-filters" data-testid="admin-filters">
          <label className="invoices__advanced-field">
            <span className="eyebrow">Société</span>
            <select
              value={tenantFilter}
              onChange={(e) => applyTenantFilter(e.target.value)}
              data-testid="admin-tenant-filter"
            >
              <option value="all">Toutes les sociétés</option>
              {tenantsInScope.map((t) => (
                <option key={t.tenantId} value={t.tenantId}>
                  {t.legalName}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* REQ-FE-029h: Entra-sync callout per mockup `09-admin-members.png`.
          Permanent informational card explaining the membership is mirrored
          from Microsoft Entra and edits here are local-scope only. */}
        <div className="admin-entra-callout" role="status" data-testid="admin-entra-callout">
          <strong>Synchronisation Entra · Pending —</strong> tu ne peux pas créer/désactiver de
          comptes ici. Synchronisez à chaque modification dans Entra.
        </div>

        {/* REQ-FE-029h: Tous / Actifs / Invités / Désactivés tabs with live
          counts. Buckets are computed client-side from membership expiresAt
          until BE adds an explicit status field (logged in REPORT). */}
        <div className="filter-pills" role="tablist" aria-label="Filtre des membres">
          {(['all', 'active', 'invited', 'disabled'] as TabKey[]).map((t) => {
            const labelFr =
              t === 'all'
                ? 'Tous'
                : t === 'active'
                  ? 'Actifs'
                  : t === 'invited'
                    ? 'Invités'
                    : 'Désactivés';
            return (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                className={`filter-pill${tab === t ? ' is-active' : ''}`}
                onClick={() => setTab(t)}
                data-testid={`admin-tab-${t}`}
              >
                <span>{labelFr}</span>
                <span className="filter-pill__count" data-testid={`admin-tab-${t}-count`}>
                  · {tabCounts[t]}
                </span>
              </button>
            );
          })}
        </div>

        {toast ? (
          <div className="invoices__error" role="status" data-testid="admin-toast">
            {toast}
          </div>
        ) : null}

        {isLoading ? (
          <p className="muted" data-testid="admin-loading">
            Chargement des membres…
          </p>
        ) : isError ? (
          <div className="invoices__error" role="alert" data-testid="admin-error">
            Impossible de charger les membres.
          </div>
        ) : visible.length === 0 ? (
          <EmptyState
            testId="admin-empty"
            variant="card"
            icon="👥"
            title="Aucun membre"
            description="Aucun membre dans le périmètre sélectionné."
          />
        ) : (
          <ul className="admin-member-list" data-testid="admin-members-list">
            {visible.map((u) => (
              <MemberRow
                key={u.uid}
                user={u}
                onEditRole={(entry) => {
                  setEditError(null);
                  setEditing({ member: entry.membership, tenant: entry.tenant });
                }}
                onRevoke={(entry) => {
                  setRevokeError(null);
                  setRevoking({ member: entry.membership, tenant: entry.tenant });
                }}
              />
            ))}
          </ul>
        )}

        {editing ? (
          <ChangeRoleModal
            member={editing.member}
            memberLabel={`${editing.member.uid} · ${editing.tenant.legalName}`}
            busy={changeRoleMut.isPending}
            errorMessage={editError}
            onSubmit={handleChangeRole}
            onClose={() => setEditing(null)}
          />
        ) : null}

        {revoking ? (
          <div
            className="modal__backdrop"
            role="presentation"
            data-testid="revoke-modal"
            onClick={(e) => {
              if (e.target === e.currentTarget) setRevoking(null);
            }}
          >
            <div className="modal" role="dialog" aria-modal="true" aria-labelledby="revoke-title">
              <header className="modal__head">
                <h2 id="revoke-title" className="display modal__title">
                  Révoquer ce membre
                </h2>
                <button
                  type="button"
                  className="modal__close"
                  onClick={() => setRevoking(null)}
                  aria-label="Fermer"
                >
                  ×
                </button>
              </header>
              <p className="muted">
                Confirmez-vous la révocation de <strong>{revoking.member.uid}</strong> sur{' '}
                <strong>{revoking.tenant.legalName}</strong> ?
              </p>
              {revokeError ? (
                <div
                  className="invoices__error"
                  role="alert"
                  data-testid="revoke-error"
                  style={{ marginTop: 8 }}
                >
                  {revokeError}
                </div>
              ) : null}
              <footer className="modal__foot">
                <button type="button" className="btn-ghost" onClick={() => setRevoking(null)}>
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={revokeMut.isPending}
                  onClick={handleRevoke}
                  data-testid="revoke-confirm"
                >
                  {revokeMut.isPending ? 'Révocation…' : 'Révoquer'}
                </button>
              </footer>
            </div>
          </div>
        ) : null}

        {inviting ? (
          <InviteMemberModal
            tenants={tenantsInScope}
            defaultTenantId={
              tenantFilter !== 'all' ? tenantFilter : (tenantsInScope[0]?.tenantId ?? '')
            }
            busy={upsertMut.isPending}
            errorMessage={inviteError}
            onSubmit={handleInvite}
            onClose={() => setInviting(false)}
          />
        ) : null}
      </section>
    </PageWrapper>
  );
}
